import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import EventCard from "@/components/EventCard";
import { formatINR } from "@/lib/sections";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";


const SPACES = [
  { to: "/gallery",    label: "Art Gallery",     hint: "Exhibitions, openings, residencies",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/od25b75l_506397686_1130193119128821_4442618323425677955_n.webp" },
  { to: "/art-centre", label: "Art Centre",      hint: "Classes, workshops, learning",
    img: "https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?auto=format&fit=crop&w=900&q=80" },
  { to: "/ceramic",    label: "Ceramic Studio",  hint: "Wheel, hand-built, glazing",
    img: "https://images.pexels.com/photos/18486408/pexels-photo-18486408.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { to: "/cafe",       label: "Cafe",            hint: "Slow food, filter coffee, garden",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/xv16g21c_Carpe%20Diem%206.webp" },
  { to: "/stage",      label: "Stage",           hint: "Live music, theatre, readings",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/w8kmnyqm_IMG20260315201701.jpg" },
  { to: "/coworking",  label: "Co-working",      hint: "Coming soon · day passes",
    img: "https://images.pexels.com/photos/7065/space-desk-office-hero-7065.jpg", comingSoon: true },
];

export default function Home() {
  const [events, setEvents] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [banners, setBanners] = useState([]);
  const [bannerIdx, setBannerIdx] = useState(0);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data.slice(0, 6))).catch(() => {});
    api.get("/events", { params: { section: "ceramic-studio" } })
      .then((r) => {
        const list = [...r.data].sort((a, b) => a.slug.localeCompare(b.slug));
        if (!list.length) return;
        const weekIndex = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
        setFeatured(list[weekIndex % list.length]);
      })
      .catch(() => {});
    api.get("/page-galleries/home-banner")
      .then(r => setBanners(r.data.photos || []))
      .catch(() => {});
  }, []);

  // Auto-rotate carousel every 6s. Pauses when only one banner exists.
  useEffect(() => {
    if (banners.length < 2) return;
    const id = setInterval(() => setBannerIdx(i => (i + 1) % banners.length), 6000);
    return () => clearInterval(id);
  }, [banners.length]);

  return (
    <div data-testid="home-page">
      {/* HOME BANNER CAROUSEL — sits right under the navbar so it's the first thing visitors see.
          Admin-managed. First slide is the property photo; more can be added for exhibition promos. */}
      {banners.length > 0 && (
        <section className="relative w-full overflow-hidden bg-charcoal" data-testid="home-banner-carousel" style={{ backgroundColor: "#1C1C19" }}>
          <div className="relative w-full h-[42vw] max-h-[560px] min-h-[280px]">
            {banners.map((b, i) => {
              const inner = (
                <>
                  <img src={b.url} alt={b.caption || "Carpe Diem banner"} className="w-full h-full object-cover" loading={i === 0 ? "eager" : "lazy"} />
                  {b.caption && (
                    <div className="absolute inset-x-0 bottom-0 p-6 md:p-10 bg-gradient-to-t from-black/70 via-black/20 to-transparent">
                      <div className="max-w-[1400px] mx-auto">
                        <p className="font-serif text-2xl md:text-3xl text-cream" style={{ color: "#F9F8F6" }}>{b.caption}</p>
                      </div>
                    </div>
                  )}
                </>
              );
              const commonCls = "absolute inset-0 transition-opacity duration-1000 ease-in-out";
              const style = { opacity: i === bannerIdx ? 1 : 0, pointerEvents: i === bannerIdx ? "auto" : "none" };
              if (b.link_url) {
                const isExternal = /^https?:\/\//i.test(b.link_url);
                if (isExternal) {
                  return (
                    <a key={i} href={b.link_url} target="_blank" rel="noreferrer noopener" className={`${commonCls} block group`} style={style} data-testid={`home-banner-slide-${i}`} aria-hidden={i !== bannerIdx}>
                      {inner}
                    </a>
                  );
                }
                return (
                  <Link key={i} to={b.link_url} className={`${commonCls} block group`} style={style} data-testid={`home-banner-slide-${i}`} aria-hidden={i !== bannerIdx}>
                    {inner}
                  </Link>
                );
              }
              return (
                <div key={i} className={commonCls} style={style} aria-hidden={i !== bannerIdx} data-testid={`home-banner-slide-${i}`}>
                  {inner}
                </div>
              );
            })}

            {/* Prev / next arrows — only when >1 banner */}
            {banners.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setBannerIdx(i => (i - 1 + banners.length) % banners.length)}
                  className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-cream/25 backdrop-blur-md hover:bg-cream/50 text-cream flex items-center justify-center transition-colors"
                  style={{ color: "#F9F8F6" }}
                  aria-label="Previous banner"
                  data-testid="home-banner-prev"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  type="button"
                  onClick={() => setBannerIdx(i => (i + 1) % banners.length)}
                  className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-cream/25 backdrop-blur-md hover:bg-cream/50 text-cream flex items-center justify-center transition-colors"
                  style={{ color: "#F9F8F6" }}
                  aria-label="Next banner"
                  data-testid="home-banner-next"
                >
                  <ChevronRight size={22} />
                </button>
                <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                  {banners.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setBannerIdx(i)}
                      className="w-2 h-2 rounded-full transition-all"
                      style={{
                        backgroundColor: i === bannerIdx ? "#F9F8F6" : "rgba(249,248,246,0.45)",
                        width: i === bannerIdx ? "22px" : "8px",
                      }}
                      aria-label={`Show banner ${i + 1}`}
                      data-testid={`home-banner-dot-${i}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* HERO */}
      <section className="relative">
        <div className="min-h-[calc(100vh-72px)]">
          <div className="relative bg-cream">
            <div className="px-6 md:px-12 lg:px-20 py-16 lg:py-24 h-full flex flex-col justify-between max-w-[1400px] mx-auto">
              <div>
                <img
                  src="https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/47po7jix_final%20logo%20no%20background%20.png"
                  alt="Carpe Diem Goa"
                  className="h-24 md:h-32 w-auto object-contain -ml-2 mb-6"
                  data-testid="home-hero-logo"
                />
                <div className="overline">An Art &amp; Learning Centre · Majorda, Goa</div>
                <h1 className="display text-5xl md:text-7xl lg:text-[7.5rem] mt-8">
                  Seize<br/>
                  <em className="italic font-light text-terra">the day.</em>
                </h1>
                <p className="mt-10 max-w-md text-base text-muted2 leading-relaxed">
                  A space where you can dare to explore the creative self that burns inside you;
                  a place to discover what others are doing with their dreams. A launchpad
                  to embark on a new journey of your own.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link to="/events" className="btn-primary" data-testid="home-hero-book">Book a workshop</Link>
                  <Link to="/gallery" className="btn-ghost" data-testid="home-hero-gallery">Visit the gallery</Link>
                </div>
              </div>
              <div className="hidden lg:flex items-end justify-between pt-12 mt-12 hairline">
                <div>
                  <div className="overline">Now showing</div>
                  <div className="font-serif text-2xl mt-2">In-House Collection</div>
                  <div className="text-xs text-muted2 mt-1">A collection of eclectic local &amp; international art</div>
                </div>
                <div>
                  <div className="overline">New chapter</div>
                  <div className="font-serif text-2xl mt-2">Co-working <em className="italic">coming soon</em></div>
                </div>
                <div>
                  <div className="overline">Est. 2012</div>
                  <div className="font-serif text-xl">Goa · India</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-soft py-4 overflow-hidden">
        <div className="marquee-track flex gap-12 whitespace-nowrap font-serif text-2xl">
          {Array(2).fill(0).map((_, i) => (
            <div key={i} className="flex gap-12">
              <span>Art</span><span className="text-terra">·</span>
              <span><em className="italic">Learning</em></span><span className="text-terra">·</span>
              <span>Clay</span><span className="text-terra">·</span>
              <span>Coffee</span><span className="text-terra">·</span>
              <span><em className="italic">Performance</em></span><span className="text-terra">·</span>
              <span>Conversation</span><span className="text-terra">·</span>
              <span>Co-working</span><span className="text-terra">·</span>
              <span><em className="italic">Stillness</em></span><span className="text-terra">·</span>
            </div>
          ))}
        </div>
      </section>

      {/* HOME BANNER CAROUSEL — admin-managed. First banner is the property photo, next slides can promote upcoming exhibitions. */}
      {/* (Rendered at the top of the page, right below the navbar — see beginning of this component.) */}


      {/* INTRO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">Introduction</div>
            <h2 className="display text-4xl md:text-5xl mt-6">
              One roof. <em className="italic text-terra">Many rooms.</em>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-3">
            <p className="text-lg leading-relaxed text-charcoal">
              The name <em className="italic">Carpe Diem</em> has been made popular by poets such as Horace and Ovid and
              through films like <em>Dead Poets Society</em>. <strong className="font-medium">Seize the day</strong> is the message our
              space would like to give. Founded in Majorda in 2012, today the centre brings together an art gallery,
              a learning centre with classes for children and adults, a fully equipped ceramic studio, a slow-food
              cafe and garden, an outdoor stage for performances — and, coming soon, a co-working space for
              creative practitioners.
            </p>
          </div>
        </div>
      </section>

      {/* SPACES BENTO */}
      <section className="bg-cream-2">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-24">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="overline">Six spaces</div>
              <h2 className="display text-4xl md:text-5xl mt-4">Where to find us.</h2>
            </div>
            <Link to="/events" className="hidden md:inline-flex items-center gap-2 text-sm link-hover" data-testid="home-all-spaces">
              See programme <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SPACES.map((s, idx) => (
              <Link
                key={s.to}
                to={s.to}
                className={`group block ${idx === 1 ? "lg:translate-y-12" : ""} ${idx === 4 ? "lg:translate-y-12" : ""}`}
                data-testid={`home-space-${s.to.replace('/', '')}`}
              >
                <div className="aspect-[4/5] overflow-hidden bg-cream border border-soft relative">
                  <img src={s.img} alt={s.label} className="reveal-img w-full h-full object-cover" loading="lazy" />
                  {s.comingSoon && (
                    <div className="absolute top-3 left-3 bg-terra text-cream px-3 py-1 text-[10px] tracking-[0.28em] uppercase font-medium" style={{ backgroundColor: "#C05C42", color: "#F9F8F6" }}>
                      Coming Soon
                    </div>
                  )}
                </div>
                <div className="mt-4 flex items-start justify-between">
                  <div>
                    <div className="overline">0{idx + 1}</div>
                    <div className="font-serif text-2xl mt-2 tracking-tight">{s.label}</div>
                    <div className="text-sm text-muted2 mt-1">{s.hint}</div>
                  </div>
                  <ArrowUpRight size={18} className="text-charcoal opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline">What&apos;s on</div>
            <h2 className="display text-4xl md:text-5xl mt-4">Upcoming.</h2>
          </div>
          <Link to="/events" className="btn-ghost" data-testid="home-events-cta">All events</Link>
        </div>

        {events.length === 0 ? (
          <div className="text-muted2">Loading upcoming events…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {events.map((e) => <EventCard key={e.id} event={e} testIdPrefix="home-event" />)}
          </div>
        )}
      </section>

      {/* FEATURED THIS WEEK — rotates through ceramic-studio courses */}
      {featured && (
        <section className="bg-cream-2" data-testid="home-featured-week">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-24 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="overline">This week · from the studio</div>
              <h2 className="display text-4xl md:text-6xl mt-6 tracking-tight">
                The <em className="italic text-terra">{featured.title.replace(/^(Introduction to |Intensive )/, '')}</em>.
              </h2>
              <div className="mt-4 font-serif text-2xl">{featured.price_inr > 0 ? formatINR(featured.price_inr) : "Free"}</div>
              <div className="text-xs text-muted2 mt-1">{featured.is_recurring ? featured.recurrence_label : ""}</div>
              <p className="mt-6 text-base text-muted2 leading-relaxed max-w-md">{featured.short_description}</p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to={`/events/${featured.slug}`} className="btn-primary" data-testid="home-featured-book">Book this course</Link>
                <Link to="/ceramic" className="btn-ghost" data-testid="home-featured-more">All pottery</Link>
              </div>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <Link to={`/events/${featured.slug}`} className="block group">
                <div className="aspect-[4/3] overflow-hidden border border-soft bg-cream">
                  <img src={featured.image_url} alt={featured.title} className="reveal-img w-full h-full object-cover" loading="lazy" />
                </div>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CO-WORKING ANNOUNCEMENT */}
      <section className="bg-charcoal text-cream" style={{ background: "#1C1C19", color: "#F9F8F6" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="overline" style={{ color: "#9F9F95" }}>New chapter</div>
            <h2 className="display text-4xl md:text-6xl mt-6">
              Co-working <em className="italic" style={{ color: "#D4A373" }}>coming soon.</em>
            </h2>
            <p className="mt-8 text-base leading-relaxed" style={{ color: "#D2D1C9" }}>
              A bright, quiet floor with fast Wi-Fi, ergonomic desks and an open garden view — perfectly
              embedded inside a gallery, a cafe and a ceramic studio. Day passes, weekly seats and a small
              number of monthly desks will be available soon. Leave your email and we&apos;ll let you know
              the moment we open.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/coworking" className="btn-ghost" style={{ borderColor: "#F9F8F6", color: "#F9F8F6" }} data-testid="home-cowork-learn">Learn more</Link>
              <Link to="/contact" className="btn-primary" data-testid="home-cowork-notify">Notify me</Link>
            </div>
          </div>
          <div className="lg:col-span-6 relative overflow-hidden">
            <img src="https://images.pexels.com/photos/7065/space-desk-office-hero-7065.jpg" alt="Co-working" className="w-full h-[420px] object-cover" />
            {/* COMING SOON diagonal ribbon */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-terra text-cream px-10 py-3 font-serif tracking-[0.35em] text-sm uppercase shadow-2xl rotate-[-6deg]"
                   style={{ backgroundColor: "#C05C42", color: "#F9F8F6", boxShadow: "0 24px 48px rgba(0,0,0,0.35)" }}>
                Coming Soon
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INSTAGRAM STRIP */}
      <section className="bg-cream-2 border-t border-soft" data-testid="home-instagram-strip">
        <a
          href="https://instagram.com/carpediemgoa"
          target="_blank"
          rel="noreferrer"
          className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-14 md:py-20 flex flex-col md:flex-row items-center gap-6 md:gap-10 group"
          data-testid="home-instagram-link"
        >
          <div className="relative shrink-0">
            {/* Halo ring — subtle brand-gradient hover shimmer */}
            <div className="absolute -inset-1.5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-md"
                 style={{ background: "linear-gradient(135deg,#FCAF45 0%,#F56040 25%,#E1306C 50%,#833AB4 75%,#405DE6 100%)" }} />
            <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-2xl p-[2.5px] transition-transform duration-500 group-hover:scale-105"
                 style={{ background: "linear-gradient(135deg,#FCAF45 0%,#F56040 25%,#E1306C 50%,#833AB4 75%,#405DE6 100%)" }}>
              <div className="w-full h-full rounded-[14px] bg-cream-2 flex items-center justify-center">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <defs>
                    <linearGradient id="ig-strip" x1="0%" y1="100%" x2="100%" y2="0%">
                      <stop offset="0%"   stopColor="#FCAF45" />
                      <stop offset="25%"  stopColor="#F56040" />
                      <stop offset="50%"  stopColor="#E1306C" />
                      <stop offset="75%"  stopColor="#833AB4" />
                      <stop offset="100%" stopColor="#405DE6" />
                    </linearGradient>
                  </defs>
                  <rect x="3" y="3" width="18" height="18" rx="5" stroke="url(#ig-strip)" strokeWidth="2" />
                  <circle cx="12" cy="12" r="4" stroke="url(#ig-strip)" strokeWidth="2" />
                  <circle cx="17.5" cy="6.5" r="1.4" fill="url(#ig-strip)" />
                </svg>
              </div>
            </div>
          </div>

          <div className="flex-1 text-center md:text-left">
            <div className="overline">Latest from</div>
            <div className="display text-3xl md:text-5xl mt-2">
              Instagram · <em className="italic text-terra">@carpediemgoa</em>
            </div>
            <p className="mt-3 text-sm md:text-base text-muted2 max-w-xl mx-auto md:mx-0 leading-relaxed">
              Class photos, poster drops, gallery openings and cafe specials — updated between visits.
            </p>
          </div>

          <div className="shrink-0 flex items-center gap-2 text-sm md:text-base font-serif italic group-hover:text-terra transition-colors">
            <span>Follow us</span>
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5">↗</span>
          </div>
        </a>
      </section>
    </div>
  );
}
